/**
 * Public "About" page — the application homepage referenced in our advertising
 * platform API access applications (Google Ads API, Meta, Pinterest).
 *
 * It has to do two jobs at once: explain the product well enough that a reviewer
 * understands what AdCanvas is and why it needs read access, and look like the
 * software it is describing. Every figure, brand name, and creative shown in the
 * product mockups below is invented for illustration — no client data appears on
 * this page, and the page makes no authenticated or network calls.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Images,
  Wallet,
  ShieldCheck,
  Layers,
  Clock,
  Check,
  ArrowRight,
} from 'lucide-react';
import { PublicPageLayout, COMPANY_NAME, PRODUCT_NAME, CONTACT_EMAIL } from './PublicPageLayout';
import './public.css';

/* Fictional demo brand used throughout the mockups. */
const DEMO_BRAND = 'Northwind Coffee Co.';

const CHANNELS = [
  { name: 'Google Ads', color: '#4f46e5' },
  { name: 'Meta', color: '#06b6d4' },
  { name: 'Pinterest', color: '#a855f7' },
];

function FrameBar({ crumb, tag }: { crumb: React.ReactNode; tag: string }) {
  return (
    <div className="ac-frame-bar">
      <div className="ac-lights"><i /><i /><i /></div>
      <div className="ac-crumb">{crumb}</div>
      <div className="ac-tag">{tag}</div>
    </div>
  );
}

/** Mockup 1 — the cross-channel overview a client sees when they log in. */
function OverviewMock() {
  const kpis = [
    { label: 'Ad spend', value: '$184,320', delta: '+6.2% vs. prior 30d' },
    { label: 'Attributed revenue', value: '$912,640', delta: '+18.4% vs. prior 30d' },
    { label: 'Blended ROAS', value: '4.95x', delta: '+0.41x vs. prior 30d' },
    { label: 'Cost per order', value: '$28.14', delta: '-9.7% vs. prior 30d' },
  ];
  const split = [
    { name: 'Google Ads', spend: '$86,410', pct: 47, color: '#4f46e5' },
    { name: 'Meta', spend: '$71,980', pct: 39, color: '#06b6d4' },
    { name: 'Pinterest', spend: '$25,930', pct: 14, color: '#a855f7' },
  ];

  const revenue = '20,130 73,122 126,126 179,110 232,114 285,98 338,92 391,96 444,78 497,70 550,58 600,44';
  const spend = '20,152 73,150 126,151 179,146 232,148 285,142 338,140 391,141 444,134 497,132 550,127 600,120';

  return (
    <div className="ac-frame">
      <FrameBar
        crumb={<><b>{DEMO_BRAND}</b> &nbsp;/&nbsp; Overview &nbsp;·&nbsp; Last 30 days</>}
        tag="Sample data"
      />
      <div className="ac-frame-body">
        <div className="ac-frame-inner">
          <div className="ac-kpis">
            {kpis.map((k) => (
              <div className="ac-kpi" key={k.label}>
                <span>{k.label}</span>
                <b>{k.value}</b>
                <em className="ac-delta">{k.delta}</em>
              </div>
            ))}
          </div>

          <div className="ac-split">
            <div className="ac-panel">
              <div className="ac-panel-head">
                <h4>Revenue vs. spend</h4>
                <div className="ac-legend">
                  <span><i className="ac-dot" style={{ background: '#4f46e5' }} /> Revenue</span>
                  <span><i className="ac-dot" style={{ background: '#06b6d4' }} /> Spend</span>
                </div>
              </div>
              <svg viewBox="0 0 620 170" width="100%" height="170" role="img" aria-label="Illustrative chart of revenue against spend, both trending upward">
                <defs>
                  <linearGradient id="ac-rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.42" />
                    <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="ac-spd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.26" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[34, 72, 110, 148].map((y) => (
                  <line key={y} x1="20" y1={y} x2="600" y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                ))}
                <polygon points={`${revenue} 600,170 20,170`} fill="url(#ac-rev)" />
                <polygon points={`${spend} 600,170 20,170`} fill="url(#ac-spd)" />
                <polyline points={spend} fill="none" stroke="#06b6d4" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                <polyline points={revenue} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                <circle cx="600" cy="44" r="4.5" fill="#4f46e5" stroke="#0b0e18" strokeWidth="2.5" />
              </svg>
            </div>

            <div className="ac-panel">
              <div className="ac-panel-head"><h4>Spend by channel</h4></div>
              <div className="ac-bars">
                {split.map((s) => (
                  <div className="ac-bar-row" key={s.name}>
                    <div><span>{s.name}</span><b>{s.spend}</b></div>
                    <div className="ac-track">
                      <div className="ac-fill" style={{ width: `${s.pct}%`, background: s.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mockup 2 — creative-level results, the view strategists spend the most time in. */
function CreativeMock() {
  const creatives = [
    { name: 'Cold Brew — UGC 15s', channel: 'Meta · Video', roas: '6.12x', spend: '$14,280', art: 'linear-gradient(140deg,#4f46e5,#a855f7)', kind: 'Video' },
    { name: 'Holiday Sampler Set', channel: 'Google · PMax', roas: '5.44x', spend: '$21,905', art: 'linear-gradient(140deg,#0ea5e9,#06b6d4)', kind: 'Image' },
    { name: 'Single Origin — Carousel', channel: 'Meta · Carousel', roas: '3.87x', spend: '$9,640', art: 'linear-gradient(140deg,#f59e0b,#ef4444)', kind: 'Carousel' },
    { name: 'Subscribe & Save', channel: 'Pinterest · Static', roas: '2.95x', spend: '$6,120', art: 'linear-gradient(140deg,#10b981,#0ea5e9)', kind: 'Image' },
  ];

  return (
    <div className="ac-frame">
      <FrameBar
        crumb={<><b>{DEMO_BRAND}</b> &nbsp;/&nbsp; Creative performance &nbsp;·&nbsp; Sorted by ROAS</>}
        tag="Sample data"
      />
      <div className="ac-frame-body">
        <div className="ac-frame-inner">
          <div className="ac-creatives">
            {creatives.map((c) => (
              <div className="ac-creative" key={c.name}>
                <div className="ac-thumb" style={{ background: c.art }}><span>{c.kind}</span></div>
                <div className="ac-creative-meta">
                  <b>{c.name}</b>
                  <em>{c.channel}</em>
                  <div className="ac-creative-stats">
                    <span>ROAS <b>{c.roas}</b></span>
                    <span>{c.spend}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mockup 3 — budget pacing, the answer to "are we on track this month?" */
function BudgetMock() {
  const rows = [
    { name: 'Google Ads — Brand', budget: '$24,000', spent: '$18,720', pace: 78, tone: '#10b981', status: 'On pace' },
    { name: 'Google Ads — Shopping', budget: '$42,000', spent: '$36,540', pace: 87, tone: '#f59e0b', status: 'Ahead' },
    { name: 'Meta — Prospecting', budget: '$38,000', spent: '$27,360', pace: 72, tone: '#10b981', status: 'On pace' },
    { name: 'Pinterest — Retargeting', budget: '$12,000', spent: '$6,840', pace: 57, tone: '#fb7185', status: 'Behind' },
  ];

  return (
    <div className="ac-frame">
      <FrameBar
        crumb={<><b>{DEMO_BRAND}</b> &nbsp;/&nbsp; Budgets &nbsp;·&nbsp; Month to date, day 23 of 30</>}
        tag="Sample data"
      />
      <div className="ac-frame-body">
        <div className="ac-frame-inner">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Budget group</th>
                <th>Monthly</th>
                <th>Spent</th>
                <th>Pacing</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td>{r.budget}</td>
                  <td>{r.spent}</td>
                  <td>
                    <span className="ac-pace">
                      <span className="ac-track">
                        <span className="ac-fill" style={{ width: `${r.pace}%`, background: r.tone, display: 'block', height: '100%' }} />
                      </span>
                      {r.pace}%
                    </span>
                  </td>
                  <td style={{ color: r.tone }}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <PublicPageLayout width="full">
      <div className="ac">
        {/* ------------------------------------------------------------ hero */}
        <header className="ac-hero">
          <div className="ac-shell ac-hero-copy">
            <span className="ac-eyebrow">Advertising reporting by {COMPANY_NAME}</span>
            <h1>Every ad dollar a client spends, <em>on one canvas.</em></h1>
            <p className="ac-lede">
              {PRODUCT_NAME} pulls campaign, creative, and conversion data out of Google Ads, Meta, and
              Pinterest and turns it into one honest picture of performance — so the people spending the
              budget and the people funding it are looking at the same numbers.
            </p>
            <div className="ac-chips">
              {CHANNELS.map((c) => (
                <span className="ac-chip" key={c.name}>
                  <i className="ac-dot" style={{ background: c.color }} />
                  {c.name}
                </span>
              ))}
              <span className="ac-chip"><i className="ac-dot" style={{ background: '#94a3b8' }} />Read-only reporting access</span>
            </div>
            <p className="ac-hero-note">
              <span className="ac-pill">Invite only</span>
              Built for {COMPANY_NAME} strategists and the clients whose accounts we manage. There is no
              public sign-up.
            </p>
          </div>

          <div className="ac-shell ac-shot">
            <OverviewMock />
            <p className="ac-caption">
              Overview dashboard. {DEMO_BRAND} is a fictional brand and every figure shown on this page is
              invented for illustration.
            </p>
          </div>
        </header>

        {/* ------------------------------------------------------- the problem */}
        <section className="ac-section">
          <div className="ac-shell">
            <div className="ac-head">
              <span className="ac-eyebrow">Why it exists</span>
              <h2>Three ad platforms, three sets of numbers, one very long Monday.</h2>
              <p>
                A brand running Google, Meta, and Pinterest at the same time has three interfaces, three
                attribution models, and three definitions of a conversion. Reconciling them by hand is a
                weekly tax on the team, and it produces reports that are already stale by the time anyone
                reads them. {PRODUCT_NAME} does that reconciliation once, automatically, and keeps it current.
              </p>
            </div>

            <div className="ac-grid ac-grid--3">
              <div className="ac-card">
                <div className="ac-icon"><Layers size={19} /></div>
                <h3>One blended view</h3>
                <p>
                  Spend, revenue, ROAS, and cost per order across every channel a brand runs — plus the
                  per-channel detail underneath, so a blended number can always be taken apart.
                </p>
              </div>
              <div className="ac-card">
                <div className="ac-icon"><Images size={19} /></div>
                <h3>Creative-level truth</h3>
                <p>
                  Results roll up to the individual ad and creative asset, with previews alongside the
                  metrics, so the next production brief is grounded in what actually worked.
                </p>
              </div>
              <div className="ac-card">
                <div className="ac-icon"><Wallet size={19} /></div>
                <h3>Budget pacing that warns you early</h3>
                <p>
                  Month-to-date pacing per budget group, so overspend and underspend surface on day nine
                  rather than in the invoice at the end of the month.
                </p>
              </div>
              <div className="ac-card">
                <div className="ac-icon"><BarChart3 size={19} /></div>
                <h3>Reporting clients actually read</h3>
                <p>
                  Weekly and monthly summaries generated from the same data the strategists work in — no
                  separate deck to assemble, and no chance of the two disagreeing.
                </p>
              </div>
              <div className="ac-card">
                <div className="ac-icon"><Clock size={19} /></div>
                <h3>Refreshed on a schedule</h3>
                <p>
                  Data syncs on a daily cadence and backfills history, so period-over-period comparisons
                  hold up instead of resetting every time a connection is re-authorized.
                </p>
              </div>
              <div className="ac-card">
                <div className="ac-icon"><ShieldCheck size={19} /></div>
                <h3>Scoped to one organization</h3>
                <p>
                  Every user belongs to specific organizations, enforced in the database itself. One client
                  can never see another client's accounts, spend, or creative.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ more product */}
        <section className="ac-section ac-section--tint">
          <div className="ac-shell">
            <div className="ac-head">
              <span className="ac-eyebrow">Inside the product</span>
              <h2>Built for the two questions clients actually ask.</h2>
              <p>
                "Which ads are working?" and "are we going to spend what we said we would?" Everything else
                in {PRODUCT_NAME} exists to answer one of those two questions faster.
              </p>
            </div>

            <div className="ac-shot">
              <CreativeMock />
              <p className="ac-caption">
                Creative performance, ranked. Thumbnails, names, and metrics shown here are invented.
              </p>
            </div>

            <div className="ac-shot">
              <BudgetMock />
              <p className="ac-caption">
                Budget pacing by group, mid-month. Budgets and spend figures shown here are invented.
              </p>
            </div>
          </div>
        </section>

        {/* -------------------------------------------------------- data flow */}
        <section className="ac-section">
          <div className="ac-shell">
            <div className="ac-head">
              <span className="ac-eyebrow">How data reaches the dashboard</span>
              <h2>What we request, and what we do with it.</h2>
              <p>
                {PRODUCT_NAME} only ever reads reporting data, and only from accounts the client already
                owns and has asked us to manage on their behalf.
              </p>
            </div>

            <div className="ac-steps">
              <div className="ac-step">
                <div>
                  <h3>The client authorizes their own account</h3>
                  <p>
                    A brand grants {COMPANY_NAME} access to the advertising account it owns, through the
                    platform's own consent flow. Authorization can be withdrawn there at any time, which
                    stops any further data from being retrieved.
                  </p>
                </div>
              </div>
              <div className="ac-step">
                <div>
                  <h3>We read reporting data on a schedule</h3>
                  <p>
                    Scheduled jobs request campaign, ad group, ad, and creative records together with their
                    performance metrics — impressions, clicks, spend, conversions, and conversion value. We
                    request read-only scopes wherever the platform offers them, and we do not create, edit,
                    or pause anything through the API.
                  </p>
                </div>
              </div>
              <div className="ac-step">
                <div>
                  <h3>It is stored against that one organization</h3>
                  <p>
                    Records are written to storage partitioned by organization and protected by row-level
                    security, so a request can only ever return data belonging to the organization the
                    signed-in user is a member of.
                  </p>
                </div>
              </div>
              <div className="ac-step">
                <div>
                  <h3>It is shown back to the people who own it</h3>
                  <p>
                    The dashboards, exports, and scheduled summaries in {PRODUCT_NAME} are the only places
                    this data goes. It is not resold, not syndicated, not used to target advertising, and
                    not used to train models.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ commitments */}
        <section className="ac-section ac-section--tint">
          <div className="ac-shell ac-narrow">
            <span className="ac-eyebrow">Data commitments</span>
            <h2 style={{ marginTop: 14 }}>The short version of our privacy policy.</h2>

            <div className="ac-checks">
              {[
                ['Read-only by design.', 'We request reporting scopes, not management scopes. AdCanvas cannot change a client’s campaigns.'],
                ['One client, one silo.', 'Data is isolated per organization at the database level. We never blend one advertiser’s data into another’s reporting.'],
                ['Never sold or brokered.', 'No advertising platform data is sold, licensed, syndicated, or shared with data brokers.'],
                ['Not used for ad targeting.', 'Data retrieved through platform APIs is used for reporting to the account owner and nothing else.'],
                ['Not used to train models.', 'We do not use client advertising data to train machine learning or AI systems.'],
                ['Deleted on request.', 'When an engagement ends, or whenever the account owner asks, we revoke connections and delete or anonymize the data.'],
                ['Google Limited Use.', 'Our use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.'],
              ].map(([strong, rest]) => (
                <div className="ac-check" key={strong}>
                  <Check size={17} strokeWidth={3} />
                  <span><strong>{strong}</strong> {rest}</span>
                </div>
              ))}
            </div>

            <div className="ac-cta">
              <h3>Want the full detail?</h3>
              <p>
                The complete policies, including retention periods, subprocessors, and how to request
                deletion, are published here.
              </p>
              <div className="ac-links">
                <Link className="ac-btn ac-btn--solid" to="/privacy">Privacy Policy <ArrowRight size={15} /></Link>
                <Link className="ac-btn ac-btn--ghost" to="/terms">Terms of Service</Link>
                <a className="ac-btn ac-btn--ghost" href={`mailto:${CONTACT_EMAIL}`}>Contact us</a>
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ who we are */}
        <section className="ac-section">
          <div className="ac-shell ac-narrow">
            <span className="ac-eyebrow">Who we are</span>
            <h2 style={{ marginTop: 14 }}>About {COMPANY_NAME}</h2>
            <div className="ac-prose" style={{ marginTop: 20 }}>
              <p>
                {COMPANY_NAME} is a marketing agency providing paid media, email, and marketing strategy
                services to consumer brands. {PRODUCT_NAME} is our own first-party software, built to run the
                reporting side of that work. It is not sold, licensed, or distributed as a standalone product
                to the general public.
              </p>
              <p>
                Questions about the platform, about how we handle data, or about a specific advertising
                account connection can be sent to{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>, and we will respond directly.
              </p>
            </div>
          </div>
        </section>
      </div>
    </PublicPageLayout>
  );
}
